import authService from './authService';

export type SessionWarningState = { showWarning: boolean; secondsLeft: number };
type WarningListener = (state: SessionWarningState) => void;

let sessionTimer: ReturnType<typeof setTimeout> | null = null;
let countdownInterval: ReturnType<typeof setInterval> | null = null;
let resetDebounceTimer: ReturnType<typeof setTimeout> | null = null;
let showWarning = false;
let secondsLeft = 0;
const listeners = new Set<WarningListener>();

function notifyListeners() {
  listeners.forEach(fn => fn({ showWarning, secondsLeft }));
}

function startCountdown() {
  if (countdownInterval) clearInterval(countdownInterval);
  secondsLeft = 60;
  notifyListeners();
  countdownInterval = setInterval(() => {
    secondsLeft = Math.max(secondsLeft - 1, 0);
    notifyListeners();
  }, 1000);
}

function stopCountdown() {
  if (countdownInterval) {
    clearInterval(countdownInterval);
    countdownInterval = null;
  }
  secondsLeft = 0;
}

function getMsLeft(): Promise<number> {
  return authService.checkAuthTime().then((res: any) => {
    const now = new Date();
    const expires = new Date(res.data.expiresUtc);
    return expires.getTime() - now.getTime();
  });
}

function startSession() {
  showWarning = false;
  stopCountdown();
  notifyListeners();
  if (sessionTimer) clearTimeout(sessionTimer);

  getMsLeft().then(ms => {
    sessionTimer = setTimeout(() => {
      getMsLeft().then(remaining => {
        if (remaining < 60000) {
          showWarning = true;
          startCountdown();
        } else {
          startSession();
        }
      });
    }, Math.max(ms - 60000, 0));
  });
}

function stopSession() {
  if (sessionTimer) {
    clearTimeout(sessionTimer);
    sessionTimer = null;
  }
  if (resetDebounceTimer) {
    clearTimeout(resetDebounceTimer);
    resetDebounceTimer = null;
  }
  stopCountdown();
  showWarning = false;
  notifyListeners();
}

// Debounced — collapses bursts of simultaneous requests into a single reset (2 s window)
function resetTimer() {
  const user = localStorage.getItem('user');
  if (!user) return;
  if (resetDebounceTimer) clearTimeout(resetDebounceTimer);
  resetDebounceTimer = setTimeout(() => {
    resetDebounceTimer = null;
    if (sessionTimer) clearTimeout(sessionTimer);
    startSession();
  }, 2000);
}

/** Subscribe to session warning state changes. Returns an unsubscribe function. */
function subscribe(fn: WarningListener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function getState(): SessionWarningState {
  return { showWarning, secondsLeft };
}

const timerService = { startSession, stopSession, resetTimer, subscribe, getState };

export default timerService;
